import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  CalendarClock,
  Video,
  Mic,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getO2OSessionStatus } from '../api/o2o';
import { trackPixelEvent } from '../hooks/useMetaPixel';

/**
 * Phase 38A.3 — O2O return page.
 *
 * Route: /o2o/return
 *
 * User lands here after Stripe Checkout (success OR cancel). We poll
 * the session status endpoint until `payment_status` transitions to
 * "paid" (webhook confirmation) or a terminal state. In mock mode the
 * frontend simulates the webhook via a client-side call so QA can
 * exercise the flow without a real Stripe integration.
 */

const fmtEUR = (cents, locale = 'it-IT') => (cents / 100).toLocaleString(locale, {
  style: 'currency', currency: 'EUR', minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
});
const fmtTime = (iso, locale = 'it-IT') => new Date(iso).toLocaleTimeString(locale, {
  hour: '2-digit', minute: '2-digit',
});

const MAX_POLLS = 15;
const POLL_INTERVAL_MS = 1200;

const O2OReturnPage = () => {
  const { t, i18n } = useTranslation('o2o');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get('status'); // "success" | "cancelled"
  const o2oId = searchParams.get('o2o_session_id');
  const isMock = searchParams.get('mock') === '1';

  const [sessionState, setSessionState] = useState({
    phase: status === 'success' ? 'polling' : 'cancelled',
    session: null,
    attempts: 0,
  });

  // Polling loop.
  useEffect(() => {
    if (sessionState.phase !== 'polling' || !o2oId) return undefined;
    let cancelled = false;

    (async () => {
      let attempt = 0;
      while (!cancelled && attempt < MAX_POLLS) {
        attempt += 1;
        try {
          const s = await getO2OSessionStatus(o2oId);
          if (cancelled) return;
          setSessionState((prev) => ({ ...prev, session: s, attempts: attempt }));
          if (s.payment_status === 'paid') {
            // Fire Meta pixel client-side (dedup event_id matches server webhook).
            trackPixelEvent(
              'Purchase',
              {
                value: (s.price_cents || 0) / 100,
                currency: 'EUR',
                content_type: 'oto',
                content_ids: [`oto_${o2oId}`],
              },
              `purchase_${o2oId}`,
            );
            setSessionState((prev) => ({ ...prev, phase: 'paid', session: s }));
            return;
          }
          if (['expired', 'cancelled'].includes(s.payment_status)) {
            setSessionState((prev) => ({ ...prev, phase: 'expired', session: s }));
            return;
          }
        } catch (_e) {
          // Ignore transient errors and keep polling.
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
      if (!cancelled) {
        setSessionState((prev) => ({ ...prev, phase: 'timeout' }));
      }
    })();

    return () => { cancelled = true; };
  }, [sessionState.phase, o2oId]);

  // MOCK-mode shortcut: in preview we don't have a real webhook, so
  // ping a helper endpoint that pretends the webhook fired. This makes
  // the flow feel instant without touching Stripe.
  // (Wired in Phase 38A.4 alongside the no-show cron; for now the UI
  // simply shows "processing" until server-side event lands.)
  useEffect(() => {
    if (!isMock || !o2oId) return;
    // No-op: the polling loop will keep spinning until the webhook is
    // fired via an admin action or the mock event simulator.
  }, [isMock, o2oId]);

  const { phase, session } = sessionState;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="o2o-return-page">
      <div className="mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-2xl">
          {phase === 'polling' && (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin gold-text" />
              <h1 className="mt-6 text-2xl font-semibold">{t('return.polling_title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('return.polling_body')}
              </p>
              {isMock && (
                <p className="mt-4 rounded-md bg-amber-500/5 p-3 text-[11px] leading-relaxed text-amber-200/80">
                  {t('return.mock_notice')}
                </p>
              )}
            </>
          )}

          {phase === 'paid' && session && (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
              <h1 className="mt-4 text-2xl font-semibold" data-testid="o2o-return-paid">
                {t('return.paid_title')}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('return.paid_body')}
              </p>
              <div className="mt-6 space-y-3 rounded-2xl border border-border bg-background/50 p-5 text-left">
                <div className="flex items-center gap-3">
                  <CalendarClock className="h-5 w-5 gold-text" />
                  <div>
                    <p className="text-sm font-medium">
                      {new Date(session.slot_start_utc).toLocaleDateString(i18n.language, {
                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {fmtTime(session.slot_start_utc, i18n.language)} – {fmtTime(session.slot_end_utc, i18n.language)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{t('return.field_mode')}</span>
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    {session.mode === 'video' ? (
                      <><Video className="h-3.5 w-3.5" /> {t('return.video')}</>
                    ) : (
                      <><Mic className="h-3.5 w-3.5" /> {t('return.audio')}</>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{t('return.field_paid')}</span>
                  <span className="text-sm font-semibold gold-text">
                    {fmtEUR(session.price_cents, i18n.language)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{t('return.field_session_id')}</span>
                  <span className="font-mono text-[11px] text-foreground/80">{session.session_id}</span>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/wallet')}
                  data-testid="o2o-return-wallet-btn"
                >
                  {t('return.wallet_cta')}
                </Button>
                <Button
                  className="gold-gradient"
                  onClick={() => navigate(`/session/${session.session_id}/waiting-room`)}
                  data-testid="o2o-return-waiting-room-btn"
                >
                  {t('return.waiting_room_cta')}
                </Button>
              </div>
            </>
          )}

          {phase === 'cancelled' && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-red-400" />
              <h1 className="mt-4 text-2xl font-semibold">{t('return.cancelled_title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('return.cancelled_body')}
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  data-testid="o2o-return-back-btn"
                >
                  {t('return.back_cta')}
                </Button>
              </div>
            </>
          )}

          {phase === 'expired' && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-orange-400" />
              <h1 className="mt-4 text-2xl font-semibold">{t('return.expired_title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('return.expired_body')}
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <Button
                  className="gold-gradient"
                  onClick={() => navigate(-1)}
                >
                  {t('return.retry_cta')}
                </Button>
              </div>
            </>
          )}

          {phase === 'timeout' && (
            <>
              <Loader2 className="mx-auto h-10 w-10 gold-text" />
              <h1 className="mt-4 text-2xl font-semibold">{t('return.timeout_title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('return.timeout_body')}
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/wallet')}
                >
                  {t('return.wallet_cta')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default O2OReturnPage;
